import * as React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { GlobalApplicationState } from 'globalApplicationState';

import { NotificationRange, Notifications as INotifications, Reminder } from "./models";

import Checkbox from "@mui/material/Checkbox";
import Drawer from "@mui/material/Drawer";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import { InfoOutlined } from "@mui/icons-material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Disclaimer from '../disclaimer';


class Notifications extends React.Component<PropsWithRedux, ComponentState> {
    constructor(props: PropsWithRedux) {
        super(props);

        this.state = this.getNotifications();
    }

    public render() {
        const { contentType } = this.props;

        const areAnyPlatformsEnabled =
            this.props.notificationSettings.settings[`${contentType}PublishedEmailAttempt`].toggle ||
            this.props.notificationSettings.settings[`${contentType}PublishedTeamsAttempt`].toggle ||
            this.props.notificationSettings.settings[`${contentType}PublishedSMSAttempt`].toggle ||
            this.props.notificationSettings.settings[`${contentType}PublishedMobileAttempt`].toggle;

        if (!this.props.show || !this.props.notifications || !this.props.notificationSettings || !areAnyPlatformsEnabled)
            return <React.Fragment></React.Fragment>;

        const { settings } = this.props.notificationSettings;

        return (
            <Drawer anchor="right" open={true} onClose={this.onClose} classes={{ paper: "authoring-drawer-panel" }}>
                <Paper square elevation={0} className="authoring-drawer-panel-header">
                    <IconButton onClick={this.onClose} size="large" id="close-notification-settings-btn">
                        <ArrowBackIcon />
                    </IconButton>
                    <div>Edit notification upon publishing</div>
                </Paper>
                <div className="authoring-drawer-panel-content">
                    <Paper elevation={0} className="publishing-notifications">
                        <div className="drawer-panel-section-header">
                            <Disclaimer
                                icon={<InfoOutlined htmlColor="#e6911a" />}
                                text={<div>Select at least one (1) channel you want to use to notify subscribers when this post gets published.</div>}
                            />
                        </div>
                            <div className="drawer-panel-section-body">
                                {settings[`${contentType}PublishedEmailAttempt`]?.toggle && (
                                    <div className="notification-setting">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    checked={this.state.emailNotificationRange !== "none"}
                                                    onChange={(e) => this.onToggleEmailEnabled(e)}
                                                    id="published-email-attempt"
                                                />
                                            }
                                            label="Email"
                                        />
                                    </div>
                                )}
                                {settings[`${contentType}PublishedMobileAttempt`]?.toggle && (
                                    <div className="notification-setting">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    checked={this.state.mobileNotificationRange !== "none"}
                                                    onChange={this.onToggleMobileEnabled}
                                                    id="published-mobile-attempt"
                                                />
                                            }
                                            label="Mobile App"
                                        />
                                    </div>
                                )}
                                {settings[`${contentType}PublishedTeamsAttempt`]?.toggle && (
                                    <div className="notification-setting">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    checked={this.state.teamsNotificationRange !== "none"}
                                                    onChange={this.onToggleTeamsEnabled}
                                                    id="published-teams-attempt"
                                                />
                                            }
                                            label="MS Teams Chatbot"
                                        />
                                    </div>
                                )}
                                {this.props.smsManagementEnabled && settings[`${contentType}PublishedSMSAttempt`]?.toggle && (
                                    <div className="notification-setting">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    checked={this.state.smsNotificationRange !== "none"}
                                                    onChange={(e) => this.onToggleSMSEnabled(e)}
                                                    id="published-sms-attempt"
                                                />
                                            }
                                            label="SMS"
                                        />
                                    </div>
                                )}
                                {
                                    this.state.emailNotificationRange === "none" && 
                                    this.state.smsNotificationRange === "none" &&
                                    this.state.teamsNotificationRange === "none" && 
                                    this.state.mobileNotificationRange === "none" &&
                                    <div className='select-channel-text-div'>
                                        <span className="select-channel-text">Select at least one (1) channel</span>
                                    </div>
                                }
                            </div>
                    </Paper>
                </div>
            </Drawer>
        );
    }

    /*
    * Gets the current notifications on first mount, if a post has previous notifications set the post notifications. If the post is previouslyPublished disable the notifications
    * else set the default notifications. If toggle is off, set the post notifications that are saved to the post.
    */
    private getNotifications = (): ComponentState => {
        const { notifications } = this.props;

        let isOldPublishedPost = this.props.isPreviouslyPublished && this.props.postPublished && new Date(this.props.postPublished).toISOString() <= new Date().toISOString();

        //Checks if our toggle is on, if it is let's use the post notifications else if it's not, let's use our default notifications
        if (this.props.notificationsToggle) {
            if (isOldPublishedPost || this.props.previousNotifications) {
                return {
                    emailNotificationRange:
                        !!this.props.previousNotifications && this.props.previousNotifications?.emailOnPublish !== "none"
                            ? this.props.previousNotifications.emailOnPublish
                            : "none",
                    mobileNotificationRange:
                        !!this.props.previousNotifications && this.props.previousNotifications.mobileOnPublish !== "none"
                            ? this.props.previousNotifications.mobileOnPublish
                            : "none",
                    smsNotificationRange:
                        !!this.props.previousNotifications && this.props.previousNotifications.smsOnPublish !== "none"
                            ? this.props.previousNotifications!.smsOnPublish
                            : "none",
                    teamsNotificationRange:
                        !!this.props.previousNotifications && this.props.previousNotifications.teamsOnPublish !== "none"
                            ? this.props.previousNotifications!.teamsOnPublish
                            : "none",
                };
            } 
            else {
                return {
                    emailNotificationRange:
                        !!this.props.defaultNotificationsState && this.props.defaultNotificationsState.emailOnPublish !== "none"
                            ? this.props.defaultNotificationsState.emailOnPublish
                            : "none",
                    mobileNotificationRange:
                        !!this.props.defaultNotificationsState && this.props.defaultNotificationsState.mobileOnPublish !== "none"
                            ? this.props.defaultNotificationsState.mobileOnPublish
                            : "none",
                    smsNotificationRange:
                        !!this.props.defaultNotificationsState && this.props.defaultNotificationsState!.smsOnPublish !== "none"
                            ? this.props.defaultNotificationsState!.smsOnPublish
                            : "none",
                    teamsNotificationRange:
                        !!this.props.defaultNotificationsState && this.props.defaultNotificationsState!.teamsOnPublish !== "none"
                            ? this.props.defaultNotificationsState!.teamsOnPublish
                            : "none",
                };
            }
        } 
        else {
            return {
                emailNotificationRange: notifications?.emailOnPublish ?? "none",
                mobileNotificationRange: notifications?.mobileOnPublish ?? "none",
                smsNotificationRange: notifications?.smsOnPublish ?? "none",
                teamsNotificationRange: notifications?.teamsOnPublish ?? "none",
            };
        }
    };

    private onApplyNotificationSettings = () => {
        const emailOnPublish: NotificationRange = this.state.emailNotificationRange;
        const mobileOnPublish: NotificationRange = this.state.mobileNotificationRange;
        const smsOnPublish: NotificationRange = this.state.smsNotificationRange;
        const teamsOnPublish: NotificationRange = this.state.teamsNotificationRange;

        const isAnyChannelsOn = emailOnPublish === "subscribers" || mobileOnPublish === "subscribers" || smsOnPublish === "subscribers" || teamsOnPublish === "subscribers";

        const reminders: Reminder[] = this.props.notifications?.reminders.filter(r => isAnyChannelsOn || r.channel !== "inherit") ?? [];

        const notifications: INotifications = {
            emailOnPublish: emailOnPublish,
            mobileOnPublish: mobileOnPublish,
            smsOnPublish: smsOnPublish,
            teamsOnPublish: teamsOnPublish,
            reminders
        };
        this.props.onChangeNotifications(notifications);
    };

    private checkPostNotificationsAreOff = () => {
        if (
            this.state.emailNotificationRange === "none" && 
            this.state.smsNotificationRange === "none" &&
            this.state.teamsNotificationRange === "none" &&
            this.state.mobileNotificationRange === "none" &&
            this.props.onToggleNotifications
        ) {
            this.props.onToggleNotifications(false);
        }
    };

    private onClose = () => {
        this.onApplyNotificationSettings();
        this.checkPostNotificationsAreOff();
        this.props.onClose();
    };

    private onToggleEmailEnabled = (e: any) => {
        if (this.state.emailNotificationRange === "none") this.setState({ emailNotificationRange: "subscribers" });
        else this.setState({ emailNotificationRange: "none" });
    };

    private onToggleMobileEnabled = () => {
        if (this.state.mobileNotificationRange === "none") this.setState({ mobileNotificationRange: "subscribers" });
        else this.setState({ mobileNotificationRange: "none" });
    };

    private onToggleTeamsEnabled = () => {
        if (this.state.teamsNotificationRange === "none") this.setState({ teamsNotificationRange: "subscribers" });
        else this.setState({ teamsNotificationRange: "none" });
    };

    private onToggleSMSEnabled = (e: any) => {
        if (this.state.smsNotificationRange === "none") this.setState({ smsNotificationRange: "subscribers" });
        else this.setState({ smsNotificationRange: "none" });
    };
}

interface INotificationsProps {
  show: boolean;
  isPreviouslyPublished: boolean;
  notifications: INotifications | undefined;
  //optional parameters for now until events authoring gets updated.
  previousNotifications?: INotifications | undefined;
  notificationsToggle?: boolean;
  postPublished?: Date | null | undefined;
  defaultNotificationsState?: INotifications | undefined;
  contentType: "post" | "event";
  onChangeNotifications: (notifications: INotifications) => void;
  onClose: () => void;
  onToggleNotifications?: (checked: boolean) => void;
}

interface ComponentState {
  emailNotificationRange: NotificationRange;
  mobileNotificationRange: NotificationRange;
  smsNotificationRange: NotificationRange;
  teamsNotificationRange: NotificationRange;
}

const connector = connect(
  (state: GlobalApplicationState, ownProps: INotificationsProps) => ({
    ...ownProps,
    notificationSettings: state.settings.notificationSettings,
    tenantSettings: state.settings.tenantSettings,
    smsManagementEnabled: state.settings.tenantSettings.showFeatures.smsManagementEnabled
  })
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(Notifications);
